import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { LanguageStore } from '../stores/LanguageStore';
import { Apps } from '../enums/Apps';
import { ISnippet } from '../interfaces/ISnippet';
import { ILanguage } from '../interfaces/ILanguage';
import { ApiService } from './ApiService';
import { IResponse } from '../interfaces/IResponse';
import { IDictionaryItem } from '../interfaces/IDictionaryItem';
import { DictionariesStore } from '../stores/DictionariesStore';
import { Dictionaries } from '../enums/Dictionaries';
import { ICurrency } from '../interfaces/ICurrency';

@Injectable({
  providedIn: 'root'
})
export class ToolsService {

  public static instance: ToolsService;

  private readonly _basePath = '/tools';
  private readonly _languageStore = inject(LanguageStore);
  private readonly _apiService = inject(ApiService);
  private readonly _dictionariesStore = inject(DictionariesStore);

  public constructor() {
    ToolsService.instance = this;
  }

  public getAllLanguages(): Observable<IResponse<ILanguage[]>> {
    return this._apiService.get(`${this._basePath}/languages`).pipe(
      map((res: IResponse<[{ items: ILanguage[]; }]>) => {
        res.data = res.data[0].items as any;

        return res as unknown as IResponse<ILanguage[]>;
      })
    );
  }

  public getAllSnippets(language: string): Observable<IResponse<ISnippet[]>> {
    return this._apiService.get(
      `${this._basePath}/snippets/all`,
      new HttpParams().append('language', language).append('app', Apps.CxCash)
    );
  }

  public addSnippet(namespace: string, value: string): Observable<IResponse<ISnippet>> {
    return this._apiService.post(
      `${this._basePath}/snippets`,
      {
        namespace,
        value,
        languageId: this._languageStore.language().id,
        appId: Apps.CxCash
      }
    );
  }

  public getDictionary(dictionary: Exclude<Dictionaries, Dictionaries.CURRENCIES>): Observable<IDictionaryItem[]> {
    return this._apiService.get(`${this._basePath}/dictionaries?dictionary=${dictionary}`)
      .pipe(
        map((res: IResponse<{ name: string; items: IDictionaryItem[]; }[]>) => res.data[0].items),
        tap(res => this._dictionariesStore[dictionary].set(res)),
      );
  }

  public getCurrencies(): Observable<ICurrency[]> {
    return this._apiService.get(`${this._basePath}/currencies`)
      .pipe(
        map((res: IResponse<ICurrency[]>) => res.data),
        tap(res => this._dictionariesStore[Dictionaries.CURRENCIES].set(res)),
      );
  }

  public clearCashCache(): Observable<void> {
    return this._apiService.post(`${this._basePath}/clear-cash-cache`);
  }
}
